"use client";

import React from "react";
import { Bar, BarChart, CartesianGrid, Cell, XAxis, YAxis } from "recharts";
import { Activity, Zap, Server, Globe } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from "@/components/ui/chart";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const benchmarkData = [{
  technology: "North Carolina",
  latency: 84064,
  icon: Zap,
  description: "Highest net migration gain",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1662596/img/pexels-nc-farm-bureau-mark-9407524.jpg"
}, {
  technology: "Texas",
  latency: 67299,
  icon: Server,
  description: "Second most popular destination",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1662596/img/ChatGPT-Image-Jan-10-2026-06_22_07-AM.png"
}, {
  technology: "South Carolina",
  latency: 66622,
  icon: Globe,
  description: "Rapidly growing coastal choice",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1662596/img/ChatGPT-Image-Jan-10-2026-06_22_07-AM.png"
}];
const chartConfig = {
  latency: {
    label: "Net Migration",
    color: "var(--color-primary)"
  }
} satisfies ChartConfig;
const CustomXAxisTick = (props: any) => {
  const {
    x,
    y,
    payload
  } = props;
  const dataItem = benchmarkData.find(d => d.technology === payload.value);
  if (!dataItem) return null;
  return <g transform={`translate(${x},${y})`}>
      <image x={-20} y={10} width={40} height={28} href={dataItem.image} style={{
      borderRadius: "4px"
    }} />
      <text x={0} y={55} textAnchor="middle" fill="white" fontSize={14} fontWeight={500} className="font-sans">
        {payload.value}
      </text>
    </g>;
};
export default function PerformanceBenchmarks() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: chartRef,
    isVisible: isChartVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  return <section data-section-id="221783" id="top-destination-rankings" className="bg-background text-foreground py-24 px-4 relative overflow-hidden">
      {}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] bg-primary/5 rounded-lg blur-[120px] pointer-events-none" />

      <div className="container mx-auto max-w-5xl relative z-10">
        <div ref={headerRef} className={`text-center mb-16 transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-primary/10 text-primary text-sm font-medium mb-6 border border-primary/20">
            <Zap className="size-4" />
            <span>Real-Time Migration Data</span>
          </div>
          <h2 className="font-default text-3xl md:text-5xl font-bold tracking-tight mb-6">
            People are leaving Illinois. <br className="hidden md:block" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-chart-4">
              Here is where they are going.
            </span>
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto font-sans">
            The data is clear: residents are seeking lower taxes, better climates, and 
            new opportunities. Our latest rankings show the top states attracting 
            former Illinoisans this year.
          </p>
        </div>

        <div ref={chartRef} className={`transition-all duration-1000 delay-200 ease-out ${isChartVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <Card className="border-white/10 bg-card/40 backdrop-blur-xl shadow-2xl shadow-black/50 overflow-hidden rounded-md">
            <CardHeader className="border-b border-white/5 pb-6 bg-white/5">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <CardTitle className="font-default text-2xl flex items-center gap-2 text-foreground">
                    <Activity className="size-6 text-primary" />
                    Top Destination Rankings
                  </CardTitle>
                  <CardDescription className="text-base mt-2 text-muted-foreground">
                    Net migration volume by state. Higher numbers indicate more residents moving in.
                  </CardDescription>
                </div>
                <div className="flex items-center gap-6 text-sm font-medium">
                  <div className="flex items-center gap-2">
                    <div className="size-3 rounded-lg bg-primary animate-pulse" />
                    <span className="text-foreground">Top Growth</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="size-3 rounded-lg bg-chart-3" />
                    <span className="text-muted-foreground">Emerging</span>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-8 pb-4 px-2 sm:px-6">
              <ChartContainer config={chartConfig} className="h-[400px] w-full">
                <BarChart data={benchmarkData} margin={{
                top: 20,
                right: 20,
                left: -20,
                bottom: 80
              }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--color-border)" opacity={0.4} />
                  <XAxis dataKey="technology" tickLine={false} axisLine={false} tick={<CustomXAxisTick />} />
                  <YAxis tickLine={false} axisLine={false} tick={{
                  fill: "var(--color-muted-foreground)",
                  fontSize: 12
                }} tickFormatter={value => `+${value.toLocaleString()}`} dx={-10} />
                  <ChartTooltip cursor={{
                  fill: "var(--color-muted)",
                  opacity: 0.1
                }} content={<ChartTooltipContent formatter={(value, name, props) => <div className="flex flex-col gap-1">
                            <span className="text-muted-foreground text-xs">{props.payload.description}</span>
                            <div className="flex items-center gap-2 font-mono text-base">
                              <span className="text-foreground font-bold">+{Number(value).toLocaleString()}</span>
                              <span className="text-muted-foreground">residents</span>
                            </div>
                          </div>} />} />
                  <Bar dataKey="latency" radius={[4, 4, 0, 0]} animationDuration={1500} animationEasing="ease-out">
                    {benchmarkData.map((entry, index) => <Cell key={`cell-${index}`} data-index={index} fill={entry.technology === "North Carolina" ? "var(--color-primary)" : entry.technology === "Texas" ? "var(--color-chart-2)" : "var(--color-chart-4)"} className="transition-all duration-300 hover:opacity-80" />)}
                  </Bar>
                </BarChart>
              </ChartContainer>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>;
}